<template>
  <b-card no-body v-if="ready" class="card-container">
    <!-- HEADER -->
    <template #header>
      <b-row class="mb-2 mt-2">
        <b-col md="6">
          <!-- Barre de recherche / filtres -->
          <b-input-group class="search-widget">
            <template #prepend>
              <b-input-group-text>
                <font-awesome-icon icon="search"></font-awesome-icon>
              </b-input-group-text>
            </template>
            <b-form-input
              type="search"
              :placeholder="$t('search_template')"
              v-model="filters.query"
            ></b-form-input>
            <template #append>
              <b-input-group-append>
                <b-button
                  variant="outline-secondary"
                  @click="showFilters = !showFilters"
                >
                  <font-awesome-icon icon="sliders-h"></font-awesome-icon>
                  {{ $t("advanced_search") }}
                </b-button>
              </b-input-group-append>
            </template>
          </b-input-group>

          <!-- Filtres avancés -->
          <div
            class="reports-filters card p-3 bg-gradient-light mt-3"
            v-show="showFilters"
          >
            <b-row>
              <b-col>
                <!-- Filtre client -->
                <b-form-select
                  v-model="filters.client"
                  class="mb-3"
                  :value="null"
                  :options="clientOptions"
                  v-if="mode === 'service provider'"
                >
                  <template #first>
                    <option :value="null">
                      {{ $t("Select client") }}
                    </option>
                  </template>
                </b-form-select>
              </b-col>
            </b-row>
          </div>
        </b-col>

        <!-- Bouton "nouveau template" -->
        <b-col md="6" class="text-right">
          <b-button
            :href="createTemplateRoute()"
            variant="primary"
            class="btn-round"
          >
            <font-awesome-icon :icon="['fas', 'plus']" />
            {{ $t("Create a new template") }}
          </b-button>
        </b-col>
      </b-row>
    </template>

    <!-- BODY -->
    <b-card-body>
      <!-- Tableau principal -->
      <b-table
        striped
        hover
        responsive
        id="report-templates-table"
        ref="reportTemplatesTable"
        :fields="fields"
        :items="getItems"
        :current-page="pagination.currentPage"
        :busy="loading"
        show-empty
        :empty-text="$t('No templates found')"
      >
        <!-- Contenu affiché quand il n'y a pas d'items -->
        <template #empty="scope">
          <div class="lead text-center py-3">
            {{ scope.emptyText }}
          </div>
        </template>

        <!-- Overlay de chargement -->
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>{{ $t("loading") }}</strong>
          </div>
        </template>

        <!-- Colonne : client -->
        <template #cell(client)="data">
          <b-link :href="clientRoute(data.item.client.id)">
            {{ data.item.client.name }}
          </b-link>
        </template>

        <!-- Colonne : columns -->
        <template #cell(columns)="data">
          {{ data.item.columns ? data.item.columns.length : 0 }}
        </template>

        <!-- Colonne : created_at -->
        <template #cell(created_at)="data">
          {{ formatDate(data.item.created_at) }}
        </template>

        <!-- Colonne : updated_at -->
        <template #cell(updated_at)="data">
          {{ formatDate(data.item.updated_at) }}
        </template>

        <!-- Colonne : actions -->
        <template #cell(actions)="data">
            <div class="text-nowrap">
                <b-button-toolbar key-nav justify style="flex-wrap: nowrap">
                    <b-button
                        size="sm"
                        variant="outline-primary"
                        :href="editRoute(data.item.id)"
                    >
                        <font-awesome-icon :icon="['fas', 'pen']" />
                        {{ $t("Edit") }}
                    </b-button>

                    <delete-button
                        @success="deleteItem(data.item.id)"
                        class="ml-2 actions-danger"
                        type="report template"
                        :title="`Delete template ${data.item.name}`"
                    ></delete-button>
                </b-button-toolbar>
            </div>   
        </template>
      </b-table>

      <!-- Modale de succès -->
      <b-modal
        id="modal-success"
        size="sm"
        title="Success"
        hide-footer
      >
        <template>
          <div class="d-flex justify-content-between flex-column">
            <h6>{{ successMessage }}</h6>
          </div>
        </template>
      </b-modal>

      <!-- Modale d'erreur -->
      <b-modal
        id="modal-error"
        size="sm"
        title="Something went wrong :("
        hide-footer
      >
        <template>
          <div class="d-flex justify-content-between flex-column">
            <h6>{{ errorMessage }}</h6>
          </div>
        </template>
      </b-modal>

      <!-- Pagination -->
      <b-pagination
        v-if="pagination.lastPage > 1"
        v-model="pagination.currentPage"
        :total-rows="pagination.total"
        :per-page="pagination.perPage"
        aria-controls="report-templates-table"
      ></b-pagination>
    </b-card-body>

    <!-- FOOTER -->
    <b-card-footer>
      <div class="text-center" v-if="pagination.total > 1">
        {{
          $t("showing_x_results", {
            current: pagination.to - pagination.from + 1,
            total: pagination.total,
          })
        }}
        <span v-if="hasFilter">
          · {{ $t("results_filtered") }} ·
          <b-link @click="resetFilters">{{ $t("clear_all") }}</b-link>
        </span>
      </div>
    </b-card-footer>
  </b-card>
</template>

<script>
import axios from "axios";
import moment from "moment";
import _ from "lodash";
import deleteButton from "../Common/DeleteButton";

export default {
  name: "ReportTemplateList",
  components: { deleteButton },
  props: {
    // Données initiales pour éviter un fetch supplémentaire
    initialTemplates: {
      type: Array,
      required: true
    },
    initialPagination: {
      type: Object,
      required: true
    },
    clients: {
      type: Array,
      required: true
    },
    // Par exemple { client: null, query: '' }
    initialFilters: {
      type: Object,
      default: () => ({})
    },
    mode: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      // Table fields
      fields: [
        { key: "id", label: this.$t("ID"), sortable: true },
        { key: "name", label: this.$t("Name"), sortable: true },
        { key: "client", label: this.$t("Client"), sortable: false },
        {
          key: "columns",
          label: this.$t("Amount of Columns"),
          sortable: false
        },
        { key: "created_at", label: this.$t("Created"), sortable: true },
        { key: "updated_at", label: this.$t("Updated"), sortable: true },
        { key: "actions", label: "", class: "align-right text-nowrap" }
      ],

      // Données
      templates: this.initialTemplates,
      filters: {
        query: "",
        client: null,
        ...this.initialFilters
      },

      // Pagination
      pagination: {
        total: this.initialPagination.total || 0,
        perPage: this.initialPagination.per_page || 15,
        currentPage: this.initialPagination.current_page || 1,
        lastPage: this.initialPagination.last_page || 1,
        from: this.initialPagination.from || 1,
        to: this.initialPagination.to || 1
      },

      // Chargement
      loading: false,
      showFilters: false,
      ready: false,

      // Messages
      successMessage: "",
      errorMessage: ""
    };
  },
  computed: {
    // Prépare la liste d'options du select client
    clientOptions() {
      // On peut aussi afficher "Tous les clients"
      return this.clients.map(c => ({
        value: c.id,
        text: c.name
      }));
    },

    // Détermine si un filtre est actif (pour afficher "results_filtered")
    hasFilter() {
      // On considère un "filtre" si query ou client sont remplis
      const { query, client } = this.filters;
      return !!(query || client);
    }
  },
  watch: {
    // Surveille les filtres et recharge la table avec debounce (500ms)
    filters: {
      handler: _.debounce(function() {
        this.reload();
      }, 500),
      deep: true
    },
    // Surveille la pagination et recharge quand on change de page
    "pagination.currentPage": {
      handler() {
        this.reload();
      }
    }
  },
  mounted() {
    // On peut imaginer un appel pour récupérer l’utilisateur actuel
    // ou d’autres ressources. Ici on se limite à passer "ready" à true
    this.ready = true;
  },
  methods: {
    /**
     * Méthode "getItems" utilisée par <b-table> (provider).
     * On utilise le pattern identique à `ReportList`.
     */
    getItems(ctx, cb) {
      // MàJ de la page
      this.pagination.currentPage = ctx.currentPage;
      this.fetchTemplates().then(() => {
        cb(this.templates);
      });
      return null;
    },

    /**
     * Recharger la table (similaire à `ReportList.reload()`)
     */
    reload() {
      this.$root.$emit("bv::refresh::table", "report-templates-table");
    },

    /**
     * Appel API pour récupérer les templates en fonction des filtres/pagination
     */
    fetchTemplates() {
      this.loading = true;

      const params = {
        page: this.pagination.currentPage,
        query: this.filters.query || "",
        client: this.filters.client
      };

      return axios
        .get(`/api/reports-templates`, { params })
        .then(response => {
          const { data, meta } = response.data;
          this.templates = data;
          this.pagination.total = meta.total;
          this.pagination.perPage = meta.per_page;
          this.pagination.currentPage = meta.current_page;
          this.pagination.lastPage = meta.last_page;
          this.pagination.from = meta.from;
          this.pagination.to = meta.to;
        })
        .catch(error => {})
        .finally(() => {
          this.loading = false;
        });
    },

    formatDate(date) {
      return moment(date).format("DD-MM-YYYY HH:mm");
    },

    /**
     * Route pour créer un template
     */
    createTemplateRoute() {
      return "/settings/templates/new";
    },

    /**
     * Route d'édition d'un template
     */
    editRoute(id) {
      return `/settings/templates/${id}/edit`;
    },

    /**
     * Route vers un client
     */
    clientRoute(slugOrId) {
      return `/companies/${slugOrId}`;
    },

    /**
     * Suppression d'un template via le composant <delete-button>
     */
    deleteItem(id) {
      axios
        .delete(`/api/reports-templates/${id}`)
        .then(() => {
          this.showSuccess({
            message: this.$t("Template deleted successfully")
          });
          this.reload();
        })
        .catch(error => {
          const errorMessage = error.response
            ? error.response.data.message
            : this.$t("unknown_error");
          this.showError({ message: errorMessage });
        });
    },

    /**
     * Bouton "Clear all" qui reset les filtres
     */
    resetFilters() {
      this.filters.query = "";
      this.filters.client = null;
    },

    /**
     * Afficher un message de succès dans la modale
     */
    showSuccess(data) {
      this.successMessage = data.message;
      this.$bvModal.show("modal-success");

      // Fermer la modale après 2s
      setTimeout(() => {
        this.$bvModal.hide("modal-success");
      }, 2000);
    },

    /**
     * Afficher un message d'erreur dans la modale
     */
    showError(data) {
      this.errorMessage = data.message;
      this.$bvModal.show("modal-error");
    }
  }
};
</script>

<style scoped>
.card-container {
  margin-bottom: 1rem;
}

/* Filtres avancés (inspiré de ReportList) */
.reports-filters {
  border: 1px solid #ddd;
  border-radius: 4px;
}

/* Overlay de chargement si nécessaire */
.loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}
</style>
